import { LAYOUT_FILTER_FIELD, isUiOnlyFilter } from '../utils/uiFilters';

export default function HeApiConnector({
  searchEndpoint,
  autocompleteEndpoint,
  downloadEndpoint,
  locationSuggestionsEndpoint,
  setParent,
}) {
  let searchState = null;

  async function onSearch(state) {
    searchState = state;

    const {
      searchTerm,
      current,
      resultsPerPage,
      sortField,
      sortDirection,
      filters,
    } = state;

    // Build up query params as an object first, then convert to a string.
    const params = new URLSearchParams();

    if (current) {
      params.set('pageNo', current);
    }

    if (resultsPerPage) {
      params.set('pageSize', resultsPerPage);
    }

    if (sortField) {
      params.set('sort', sortField);
    }
    if (sortDirection) {
      params.set('sortDirection', sortDirection);
    }

    if (searchTerm) {
      params.set('searchTerm', searchTerm);
    }

    // Convert Search UI filters into repeated query params.
    if (Array.isArray(filters)) {
      filters.forEach(filter => {
        if (isUiOnlyFilter(filter.field)) return;
        const { field, values } = filter;

        values.forEach(val => {
          params.append(field, val);
        });
      });
    }

    const layoutFilter = filters?.find(f => f.field === LAYOUT_FILTER_FIELD);
    if (layoutFilter?.values?.[0] === 'map') {
      params.set('mapSearch', 'true');
    }

    const response = await fetch(`${searchEndpoint}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(
        `Search API error: ${response.status} - ${response.statusText}`,
      );
    }

    const data = await response.json();

    return buildSearchResponse(data);
  }

  /**
   * Handle autocomplete requests against /autocompletenhle.
   */
  async function onAutocomplete(autoCompleteTerm, autoCompleteField) {
    const { searchTerm, filters } = searchState;

    const params = new URLSearchParams();
    params.set('autoCompleteTerm', autoCompleteTerm);

    if (autoCompleteField) {
      params.set('autoCompleteField', autoCompleteField);
    }

    if (searchTerm) {
      params.set('searchTerm', searchTerm);
    }

    if (Array.isArray(filters)) {
      filters.forEach(filter => {
        if (isUiOnlyFilter(filter.field)) return;
        const { field, values } = filter;

        if (autoCompleteField && autoCompleteField === field) {
          return;
        }

        values.forEach(val => {
          params.append(field, val);
        });
      });
    }

    params.set('size', '50');

    const response = await fetch(
      `${autocompleteEndpoint}?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error(
        `Autocomplete API error: ${response.status} - ${response.statusText}`,
      );
    }

    const suggestions = await response.json();

    // Return the suggestions in the shape React Search UI expects
    const autocompletedResults = suggestions.map(text => ({
      suggestion: text,
    }));

    return { autocompletedResults };
  }

  /**
   * Fetch location suggestions from the GIS endpoint, which returns a plain
   * array of strings (unlike the autocomplete endpoint).
   */
  async function onLocationSuggestions(location) {
    const term = (location || '').trim();
    if (!term || !locationSuggestionsEndpoint) {
      return [];
    }

    const params = new URLSearchParams();
    params.set('location', term);

    const response = await fetch(
      `${locationSuggestionsEndpoint}?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error(
        `Location suggestions API error: ${response.status} - ${response.statusText}`,
      );
    }

    const suggestions = await response.json();

    return Array.isArray(suggestions) ? suggestions : [];
  }

  async function onDownloadResults() {
    const {
      searchTerm,
      current,
      resultsPerPage,
      sortField,
      sortDirection,
      filters,
    } = searchState;

    // Build up query params as an object first, then convert to a string.
    const params = new URLSearchParams();

    if (current) {
      params.set('pageNo', current);
    }

    if (resultsPerPage) {
      params.set('pageSize', resultsPerPage);
    }

    if (sortField) {
      params.set('sort', sortField);
    }
    if (sortDirection) {
      params.set('sortDirection', sortDirection);
    }

    if (searchTerm) {
      params.set('searchTerm', searchTerm);
    }

    // Convert Search UI filters into repeated query params.
    if (Array.isArray(filters)) {
      filters.forEach(filter => {
        if (isUiOnlyFilter(filter.field)) return;
        const { field, values } = filter;

        values.forEach(val => {
          params.append(field, val);
        });
      });
    }

    window.location.href = `${downloadEndpoint}?${params.toString()}`;
  }

  /**
   * Convert the returned JSON from the /search endpoint into the standard shape that @elastic/react-search-ui expects.
   */
  function buildSearchResponse(data) {
    if (data.parent && setParent) {
      setParent(data.parent);
    }

    const recordList = Array.isArray(data.records) ? data.records : [];
    const results = recordList.map((record, index) => {
      const id = record.id || Math.random().toString(36).substring(2, 11);
      return {
        // The '_meta' field is required in React Search UI
        _meta: {
          id: id,
          resultNumber: index + 1,
        },
        // Ensure id field exists with raw structure for React Search UI's internal use
        id: { raw: id },
        ...record,
      };
    });

    const facets = buildFacets(data.aggregations);
    if (data.totalMapRecords != null) {
      facets.totalMapRecords = data.totalMapRecords;
    }
    // GeoJSON geometry of the searched location (when a location filter is
    // applied) — lets the map pan to the place itself when it has no results.
    if (data.geoShape != null) {
      facets.locationGeoShape = data.geoShape;
    }

    // Return an object to match ResponseState.
    return {
      requestId: new Date().getTime().toString(),
      facets: facets,
      totalPages: data.pages || 0,
      totalResults: data.totalRecords || 0,
      results: results,
    };
  }

  /**
   * Convert each AggregationList entry into a standard 'facets' object.
   *
   * Search UI expects:
   * {
   *   grade: [
   *     {
   *       type: "value",
   *       data: [
   *         { value: "II*", count: 1 },
   *         { value: "I", count: 12 },
   *         ...
   *       ]
   *     }
   *   ],
   *   region: [
   *     {
   *       type: "value",
   *       data: [
   *         { value: "South East", count: 5 },
   *         ...
   *       ]
   *     }
   *   ]
   * }
   */
  function buildFacets(aggregationList) {
    if (!Array.isArray(aggregationList)) return {};

    const facets = {};

    for (const agg of aggregationList) {
      const fieldName = agg.key;
      const buckets = agg.values || {};

      // Convert the { value: count } object into an array of { value, count }
      const facetData = Object.entries(buckets).map(([val, count]) => ({
        value: val,
        count,
      }));

      facets[fieldName] = [
        {
          type: 'value',
          data: facetData,
        },
      ];
    }

    return facets;
  }

  return {
    maxResults: () => 10000,
    onSearch,
    onAutocomplete,
    onLocationSuggestions,
    onDownloadResults,
    onResultClick: () => {
      // no-op or implement analytics
    },
    onAutocompleteResultClick: () => {
      // no-op or implement analytics
    },
  };
}
